import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperAdmin, getRoleDisplayName } from '../../utils/roles';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';

interface User {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  national_id?: string | null;
  role: 'super_admin' | 'group_creator' | 'group_admin' | 'group_clerk' | 'group_member' | 'member';
  group_roles?: Array<'admin' | 'clerk' | 'member'>;
  created_at: string;
}

const AccountSettings = () => {
  const { userProfile, user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdminUser = isSuperAdmin(userProfile?.role);

  // Account creation state
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [createAccountData, setCreateAccountData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'member' as 'group_admin' | 'member',
  });
  const [createAccountLoading, setCreateAccountLoading] = useState(false);
  const [createAccountErrors, setCreateAccountErrors] = useState<Record<string, string>>({});

  // Password reset state (for other users)
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    emailOrPhone: '',
  });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string>('');

  // Super admin: set/reset password for any user (server-side via Edge Function)
  const [showAdminSetUserPassword, setShowAdminSetUserPassword] = useState(false);
  const [adminPasswordTargetUser, setAdminPasswordTargetUser] = useState<User | null>(null);
  const [adminSetPasswordData, setAdminSetPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [adminSetPasswordLoading, setAdminSetPasswordLoading] = useState(false);
  const [adminSetPasswordError, setAdminSetPasswordError] = useState<string>('');

  // Change own password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordErrors, setChangePasswordErrors] = useState<Record<string, string>>({});

  // Super admin: edit user profile
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserData, setEditUserData] = useState({
    fullName: '',
    phone: '',
    nationalId: '',
    role: 'group_member' as 'super_admin' | 'group_creator' | 'group_admin' | 'group_clerk' | 'group_member',
  });
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserError, setEditUserError] = useState<string>('');

  // Users list state (for super_admin)
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Personal information state
  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    phone: '',
    nationalId: '',
    avatarUrl: '',
  });
  const [editingPersonalInfo, setEditingPersonalInfo] = useState(false);
  const [savingPersonalInfo, setSavingPersonalInfo] = useState(false);
  const [personalInfoError, setPersonalInfoError] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = React.useRef<HTMLInputElement>(null);

  // Company profile state (for group_admin)
  const [companyName, setCompanyName] = useState('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loadingCompanyProfile, setLoadingCompanyProfile] = useState(false);
  const companyLogoInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSuperAdminUser) {
      loadUsers();
    } else {
      loadPersonalInfo();
      if (userProfile?.role === 'group_admin') {
        loadCompanyProfile();
      }
    }
  }, [isSuperAdminUser, userProfile?.role, userProfile?.id]);

  const loadPersonalInfo = async () => {
    if (!userProfile?.id) return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('full_name, phone, national_id, avatar_url')
        .eq('id', userProfile.id)
        .single();

      if (error) throw error;

      if (data) {
        setPersonalInfo({
          fullName: data.full_name || '',
          phone: data.phone || '',
          nationalId: data.national_id || '',
          avatarUrl: data.avatar_url || '',
        });
        setAvatarPreview(data.avatar_url || null);
      }
    } catch (error: any) {
      console.error('Error loading personal info:', error);
      setPersonalInfoError('Failed to load personal information');
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile || !user?.id) return;

    setUploadingAvatar(true);
    try {
      const fileExtension = avatarFile.name.split('.').pop() || 'jpg';
      const fileName = `avatar_${Date.now()}.${fileExtension}`;
      const filePath = `${user.id}/${fileName}`;

      // Delete old avatar if exists
      if (personalInfo.avatarUrl && personalInfo.avatarUrl.includes('avatars')) {
        try {
          const uri = new URL(personalInfo.avatarUrl);
          const pathSegments = uri.pathname.split('/');
          const avatarIndex = pathSegments.indexOf('avatars');
          if (avatarIndex !== -1 && pathSegments.length > avatarIndex + 2) {
            const oldUserId = pathSegments[avatarIndex + 1];
            const oldFileName = pathSegments[avatarIndex + 2];
            const oldFilePath = `${oldUserId}/${oldFileName}`;
            await supabase.storage.from('avatars').remove([oldFilePath]);
          }
        } catch (e) {
          console.warn('Failed to delete old avatar:', e);
        }
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: true,
          contentType: avatarFile.type,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      await handleSavePersonalInfo(publicUrl);
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      setPersonalInfoError(error.message || 'Failed to upload avatar. Please try again.');
      setUploadingAvatar(false);
    }
  };

  const handleSavePersonalInfo = async (newAvatarUrl?: string) => {
    if (!user?.id) {
      setPersonalInfoError('User not authenticated');
      return;
    }

    setSavingPersonalInfo(true);
    setPersonalInfoError('');

    try {
      // If avatar file is selected but not uploaded yet, upload it first
      if (avatarFile && !newAvatarUrl) {
        await handleUploadAvatar();
        return;
      }

      // Direct update - RLS policies should allow users to update their own profile
      const updateData: any = {
        full_name: personalInfo.fullName.trim() || null,
        phone: personalInfo.phone.trim() || null,
        national_id: personalInfo.nationalId.trim() || null,
        updated_at: new Date().toISOString(),
      };

      // Only update avatar_url if it's being changed
      if (newAvatarUrl !== undefined) {
        updateData.avatar_url = newAvatarUrl || null;
      }

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) {
        console.error('Update error:', error);
        throw error;
      }

      setEditingPersonalInfo(false);
      setAvatarFile(null);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      await loadPersonalInfo();
      alert('Personal information updated successfully!');
    } catch (error: any) {
      console.error('Error updating personal info:', error);
      setPersonalInfoError(error.message || 'Failed to update personal information. Please try again.');
    } finally {
      setSavingPersonalInfo(false);
    }
  };

  const loadCompanyProfile = async () => {
    if (!userProfile?.id) return;
    try {
      setLoadingCompanyProfile(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('company_name, company_logo_url')
        .eq('id', userProfile.id)
        .single();

      if (error) throw error;
      setCompanyName(data?.company_name || '');
      setCompanyLogoUrl(data?.company_logo_url || null);
      setLogoPreview(data?.company_logo_url || null);
    } catch (error: any) {
      console.error('Error loading company profile:', error);
    } finally {
      setLoadingCompanyProfile(false);
    }
  };

  const handleCompanyLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadCompanyLogo = async () => {
    if (!logoFile || !user?.id) return;

    setUploadingLogo(true);
    try {
      const fileExtension = logoFile.name.split('.').pop() || 'jpg';
      const fileName = `${user.id}/${Date.now()}-logo.${fileExtension}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(fileName, logoFile, {
          cacheControl: '3600',
          upsert: true,
          contentType: logoFile.type,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(fileName);

      // Save company profile
      await handleSaveCompanyProfile(publicUrl);
    } catch (error: any) {
      console.error('Logo upload error:', error);
      alert(error.message || 'Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveCompanyLogo = async () => {
    if (!companyLogoUrl || !user?.id) return;

    try {
      // Extract file path from URL
      const urlParts = companyLogoUrl.split('/');
      const logoIndex = urlParts.indexOf('company-logos');
      if (logoIndex !== -1 && urlParts.length > logoIndex + 2) {
        const userId = urlParts[logoIndex + 1];
        const fileName = urlParts[logoIndex + 2];
        const filePath = `${userId}/${fileName}`;

        // Delete from storage
        const { error } = await supabase.storage
          .from('company-logos')
          .remove([filePath]);

        if (error) throw error;
      }

      // Update profile to remove logo URL
      await handleSaveCompanyProfile(null);
    } catch (error: any) {
      console.error('Logo removal error:', error);
      alert(error.message || 'Failed to remove logo. Please try again.');
    }
  };

  const handleSaveCompanyProfile = async (logoUrl?: string | null) => {
    if (!userProfile?.id || !user?.id) {
      alert('User not authenticated. Please log in again.');
      return;
    }
    
    // Ensure we're using the authenticated user's ID
    const userId = user.id;

    try {
      // Build update data - only include fields we want to update
      const updateData: {
        company_name: string | null;
        company_logo_url?: string | null;
      } = {
        company_name: companyName.trim() || null,
      };
      
      // Only include logo_url if it's being updated
      if (logoUrl !== undefined) {
        updateData.company_logo_url = logoUrl;
      }

      console.log('Updating company profile:', { userId, updateData });

      // First verify the user can read their own profile
      const { data: profileCheck, error: checkError } = await supabase
        .from('user_profiles')
        .select('id, company_name, company_logo_url')
        .eq('id', userId)
        .single();

      if (checkError) {
        console.error('Error checking profile:', checkError);
        throw new Error(`Cannot access profile: ${checkError.message}`);
      }

      if (!profileCheck) {
        throw new Error('Profile not found');
      }

      console.log('Profile check passed, updating via RPC function...');

      // Use RPC function to update company profile (bypasses RLS issues)
      // Only pass parameters that are defined (not undefined)
      const rpcParams: any = {
        p_company_name: updateData.company_name,
      };
      
      // Only include logo_url if it's explicitly set (not undefined)
      if (logoUrl !== undefined) {
        rpcParams.p_company_logo_url = logoUrl;
      }

      console.log('RPC params:', rpcParams);

      const { data: result, error } = await supabase.rpc('update_company_profile', rpcParams);

      if (error) {
        console.error('RPC update error:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('Update successful via RPC:', result);

      // Update local state
      setCompanyLogoUrl(logoUrl !== undefined ? logoUrl : companyLogoUrl);
      setLogoFile(null);
      if (companyLogoInputRef.current) companyLogoInputRef.current.value = '';
      
      // Reload company profile to ensure sync
      await loadCompanyProfile();
      
      alert('Company profile updated successfully! This will be used for all new groups you create.');
    } catch (error: any) {
      console.error('Error updating company profile:', error);
      const errorMessage = error.message || error.error?.message || 'Failed to update company profile. Please try again.';
      alert(`Error: ${errorMessage}\n\nIf this persists, please check:\n1. You are logged in\n2. Your session is valid\n3. Try refreshing the page`);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, national_id, role, created_at')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading users:', error);
        throw error;
      }

      console.log('Loaded users from database:', data?.length, 'users');
      if (data && data.length > 0) {
        const usersWithNationalId = data.filter((u: any) => u.national_id);
        console.log('Users with national_id:', usersWithNationalId.length);
        console.log('Sample users with national_id:', usersWithNationalId.slice(0, 3).map((u: any) => ({
          id: u.id,
          name: u.full_name,
          national_id: u.national_id
        })));
      }

      const usersData: User[] = (data || []).map((u: any) => ({
        ...u,
        group_roles: [],
      }));

      // Enrich with group_roles (admin/clerk/member) from group_members.
      // Note: "clerk" is a GROUP role (public.member_role), not a platform role (public.user_role),
      // so it won't appear in user_profiles.role.
      try {
        const { data: memberRoles, error: memberRolesError } = await supabase
          .from('group_members')
          .select('user_id, role');

        if (!memberRolesError && memberRoles) {
          const rolesByUserId = new Map<string, Set<'admin' | 'clerk' | 'member'>>();
          for (const row of memberRoles as any[]) {
            const uid = row.user_id as string | undefined;
            const role = row.role as 'admin' | 'clerk' | 'member' | undefined;
            if (!uid || !role) continue;
            if (!rolesByUserId.has(uid)) rolesByUserId.set(uid, new Set());
            rolesByUserId.get(uid)!.add(role);
          }

          usersData.forEach((u) => {
            const set = rolesByUserId.get(u.id);
            if (set && set.size) {
              // Order roles for display: admin > clerk > member
              const ordered: Array<'admin' | 'clerk' | 'member'> = [];
              if (set.has('admin')) ordered.push('admin');
              if (set.has('clerk')) ordered.push('clerk');
              if (set.has('member')) ordered.push('member');
              u.group_roles = ordered;
            }
          });
        }
      } catch (e) {
        console.warn('Failed to load group member roles:', e);
      }

      setUsers(usersData);
    } catch (error: any) {
      console.error('Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateAccountErrors({});

    // Validation
    if (!createAccountData.fullName.trim()) {
      setCreateAccountErrors({ fullName: 'Full name is required' });
      return;
    }

    if (!createAccountData.email.trim() && !createAccountData.phone.trim()) {
      setCreateAccountErrors({ general: 'Either email or phone is required' });
      return;
    }

    if (createAccountData.password.length < 6) {
      setCreateAccountErrors({ password: 'Password must be at least 6 characters' });
      return;
    }

    if (createAccountData.password !== createAccountData.confirmPassword) {
      setCreateAccountErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }

    setCreateAccountLoading(true);

    try {
      let authEmail: string;
      let userMetadata: any = {
        full_name: createAccountData.fullName,
        role: createAccountData.role,
      };

      if (createAccountData.email.trim()) {
        // Use email
        authEmail = createAccountData.email.trim();
      } else {
        // Use phone
        let formattedPhone = createAccountData.phone.trim();
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          formattedPhone = '+678' + formattedPhone;
        }
        const cleanedPhone = formattedPhone.replace(/[^\d]/g, '');
        authEmail = `phone_${cleanedPhone}@phone.local`;
        userMetadata.phone = formattedPhone;
      }

      // Create auth user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: authEmail,
        password: createAccountData.password,
        options: {
          data: userMetadata,
        },
      });

      if (signUpError) throw signUpError;

      if (!authData.user) {
        throw new Error('Account creation failed');
      }

      // Update user profile with role
      let phoneValue: string | null = null;
      if (createAccountData.phone.trim() && !createAccountData.email.trim()) {
        let formattedPhone = createAccountData.phone.trim();
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          formattedPhone = '+678' + formattedPhone;
        }
        phoneValue = formattedPhone;
      }

      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          role: createAccountData.role,
          full_name: createAccountData.fullName,
          email: createAccountData.email.trim() || null,
          phone: phoneValue,
        })
        .eq('id', authData.user.id);

      if (profileError) {
        console.error('Failed to update profile:', profileError);
        // Still show success as auth user was created
      }

      // Create notification for super_admin about new account creation
      try {
        const { data: superAdmins, error: superAdminError } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .eq('role', 'super_admin');

        if (!superAdminError && superAdmins && superAdmins.length > 0) {
          const notificationPromises = superAdmins.map(async (admin) => {
            const { error } = await supabase
              .from('notifications')
              .insert({
                user_id: admin.id,
                type: 'member_joined',
                title: 'New Account Created',
                message: `Super admin created new ${createAccountData.role === 'group_admin' ? 'group admin' : 'member'} account: ${createAccountData.fullName}${createAccountData.email ? ` (${createAccountData.email})` : createAccountData.phone ? ` (${createAccountData.phone})` : ''}`,
                related_entity_id: authData.user!.id,
                is_read: false,
              });

            if (error) {
              console.error(`Error creating notification for super admin ${admin.id}:`, error);
            }
          });

          await Promise.all(notificationPromises);
        }
      } catch (notifError) {
        console.error('Error creating super admin notifications for account creation:', notifError);
      }

      // Success
      setShowCreateAccount(false);
      setCreateAccountData({
        fullName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        role: 'member',
      });
      await loadUsers(); // Refresh users list

      alert('Account created successfully!');
    } catch (error: any) {
      console.error('Create account error:', error);
      setCreateAccountErrors({
        general: error.message || 'Failed to create account. Please try again.',
      });
    } finally {
      setCreateAccountLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetPasswordError('');

    if (!resetPasswordData.emailOrPhone.trim()) {
      setResetPasswordError('Email or phone number is required');
      return;
    }

    setResetPasswordLoading(true);

    try {
      // Determine if input is email or phone
      const isEmailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetPasswordData.emailOrPhone);
      let resetEmail = resetPasswordData.emailOrPhone;

      // If it's a phone number, convert to auth email format
      if (!isEmailFormat) {
        let formattedPhone = resetPasswordData.emailOrPhone.trim();
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          formattedPhone = '+678' + formattedPhone;
        }
        const cleanedPhone = formattedPhone.replace(/[^\d]/g, '');
        resetEmail = `phone_${cleanedPhone}@phone.local`;
      }

      // Send password reset email via SendGrid
      // window.location.origin already includes the port if present (e.g., http://172.16.20.104:4028)
      const redirectUrl = `${window.location.origin}/login?reset=true`;
      
      // Save the current origin for redirect handling in case Supabase redirects to wrong origin
      if (window.location.origin) {
        sessionStorage.setItem('app_origin', window.location.origin);
      }
      
      const { data, error } = await supabase.functions.invoke('send-password-reset-email', {
        body: {
          emailOrPhone: resetPasswordData.emailOrPhone,
          redirectUrl,
        },
      });

      if (error) throw error;
      if (!data?.ok) {
        throw new Error(data?.error || 'Failed to send password reset email');
      }

      setShowResetPassword(false);
      setResetPasswordData({ emailOrPhone: '' });
      alert('Password reset email sent successfully!');
    } catch (error: any) {
      console.error('Reset password error:', error);
      setResetPasswordError(error.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const openEditUserModal = (target: User) => {
    setEditingUser(target);
    setEditUserData({
      fullName: target.full_name || '',
      phone: target.phone || '',
      nationalId: target.national_id || '',
      role: (target.role === 'member' ? 'group_member' : target.role) as 'super_admin' | 'group_creator' | 'group_admin' | 'group_clerk' | 'group_member',
    });
    setEditUserError('');
    setShowEditUser(true);
  };

  const closeEditUserModal = () => {
    setShowEditUser(false);
    setEditingUser(null);
    setEditUserData({
      fullName: '',
      phone: '',
      nationalId: '',
      role: 'group_member',
    });
    setEditUserError('');
  };

  const handleSaveUserEdit = async () => {
    if (!editingUser) return;

    setEditUserLoading(true);
    setEditUserError('');

    try {
      // Update user profile
      const trimmedNationalId = editUserData.nationalId.trim();
      const updateData: any = {
        full_name: editUserData.fullName.trim() || null,
        phone: editUserData.phone.trim() || null,
        national_id: trimmedNationalId || null, // Keep empty string as null
        role: editUserData.role,
        updated_at: new Date().toISOString(),
      };
      
      console.log('Preparing update - national_id value:', trimmedNationalId, 'will be saved as:', trimmedNationalId || null);

      console.log('Updating user:', editingUser.id, 'with data:', updateData);

      // Use RPC function for super_admin to bypass RLS
      const { data: updatedData, error } = await supabase.rpc('update_user_profile', {
        p_user_id: editingUser.id,
        p_full_name: editUserData.fullName.trim() || null,
        p_phone: editUserData.phone.trim() || null,
        p_national_id: trimmedNationalId || null,
        p_role: editUserData.role || null,
      });

      if (error) {
        console.error('Update error:', error);
        throw error;
      }

      console.log('Update successful, returned data:', updatedData);
      console.log('Updated national_id:', updatedData?.national_id);

      // Force refresh by clearing cache and reloading
      setUsers([]);
      await loadUsers();
      
      // Small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 200));
      
      closeEditUserModal();
      alert('User information updated successfully!');
    } catch (error: any) {
      console.error('Error updating user:', error);
      setEditUserError(error.message || 'Failed to update user information. Please try again.');
    } finally {
      setEditUserLoading(false);
    }
  };

  const openAdminSetPasswordModal = (target: User) => {
    setAdminPasswordTargetUser(target);
    setAdminSetPasswordError('');
    setAdminSetPasswordData({ newPassword: '', confirmPassword: '' });
    setShowAdminSetUserPassword(true);
  };

  const generateTempPassword = () => {
    // Simple, reasonably strong temp password for admin use
    const rand = (len: number) => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
      let out = '';
      for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
      return out;
    };
    return `Temp#${rand(10)}`;
  };

  const handleAdminSetUserPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdminSetPasswordError('');

    if (!adminPasswordTargetUser?.id) {
      setAdminSetPasswordError('No user selected');
      return;
    }

    if (adminSetPasswordData.newPassword.length < 8) {
      setAdminSetPasswordError('Password must be at least 8 characters');
      return;
    }

    if (adminSetPasswordData.newPassword !== adminSetPasswordData.confirmPassword) {
      setAdminSetPasswordError('Passwords do not match');
      return;
    }

    setAdminSetPasswordLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-reset-password', {
        body: {
          userId: adminPasswordTargetUser.id,
          newPassword: adminSetPasswordData.newPassword,
        },
      });

      if (error) throw error;
      if (!data?.ok) {
        throw new Error(data?.error || 'Password reset failed');
      }

      setShowAdminSetUserPassword(false);
      setAdminPasswordTargetUser(null);
      setAdminSetPasswordData({ newPassword: '', confirmPassword: '' });
      alert('Password updated successfully!');
    } catch (error: any) {
      console.error('Admin reset password error:', error);
      // Improve error visibility (supabase-js wraps function failures in FunctionsHttpError with a Response context)
      let message =
        error?.message ||
        'Failed to reset password. Ensure the Edge Function is deployed and SUPABASE_SERVICE_ROLE_KEY is set.';

      try {
        const ctx = error?.context;
        // In supabase-js, ctx is often a Response for FunctionsHttpError
        if (ctx && typeof ctx.status === 'number') {
          const status = ctx.status;
          let bodyText = '';
          try {
            bodyText = await ctx.text();
          } catch {
            bodyText = '';
          }
          message = `Edge Function error (HTTP ${status})${bodyText ? `: ${bodyText}` : ''}`;
        }
      } catch {
        // ignore diagnostic failures
      }

      setAdminSetPasswordError(message);
    } finally {
      setAdminSetPasswordLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePasswordErrors({});

    // Validation
    if (!changePasswordData.currentPassword) {
      setChangePasswordErrors({ currentPassword: 'Current password is required' });
      return;
    }

    if (changePasswordData.newPassword.length < 6) {
      setChangePasswordErrors({ newPassword: 'Password must be at least 6 characters' });
      return;
    }

    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setChangePasswordErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }

    if (changePasswordData.currentPassword === changePasswordData.newPassword) {
      setChangePasswordErrors({ newPassword: 'New password must be different from current password' });
      return;
    }

    setChangePasswordLoading(true);

    try {
      // First, verify current password by attempting to sign in
      if (!user?.email) {
        throw new Error('User email not found');
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: changePasswordData.currentPassword,
      });

      if (signInError) {
        setChangePasswordErrors({ currentPassword: 'Current password is incorrect' });
        return;
      }

      // If current password is correct, update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: changePasswordData.newPassword,
      });

      if (updateError) throw updateError;

      // Success
      setShowChangePassword(false);
      setChangePasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      alert('Password changed successfully!');
    } catch (error: any) {
      console.error('Change password error:', error);
      setChangePasswordErrors({
        general: error.message || 'Failed to change password. Please try again.',
      });
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.phone?.toLowerCase().includes(query) ||
      user.national_id?.toLowerCase().includes(query) ||
      user.group_roles?.some((r) => r.toLowerCase().includes(query)) ||
      user.role?.toLowerCase().includes(query)
    );
  });

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'group_admin':
        return 'bg-blue-100 text-blue-800';
      case 'member':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Super admin logo upload state
  const [superAdminLogoFile, setSuperAdminLogoFile] = useState<File | null>(null);
  const [superAdminLogoPreview, setSuperAdminLogoPreview] = useState<string>('');
  const [superAdminLogoUploading, setSuperAdminLogoUploading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState<string>('/assets/images/ihslogo.png');

  useEffect(() => {
    // Load current logo from localStorage or settings
    const savedLogo = localStorage.getItem('customLogo');
    if (savedLogo) {
      setCurrentLogo(savedLogo);
    }
  }, []);

  const handleSuperAdminLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setSuperAdminLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setSuperAdminLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSuperAdminLogoUpload = async () => {
    if (!superAdminLogoFile) return;

    setSuperAdminLogoUploading(true);
    try {
      const fileName = `logo_${Date.now()}_${superAdminLogoFile.name}`;
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(fileName, superAdminLogoFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      // Save to localStorage and state
      localStorage.setItem('customLogo', publicUrl);
      setCurrentLogo(publicUrl);
      setSuperAdminLogoFile(null);
      setSuperAdminLogoPreview('');
      
      alert('Logo uploaded successfully!');
    } catch (error: any) {
      console.error('Logo upload error:', error);
      alert(error.message || 'Failed to upload logo. Please try again.');
    } finally {
      setSuperAdminLogoUploading(false);
    }
  };

  const handleSuperAdminLogoReset = () => {
    localStorage.removeItem('customLogo');
    setCurrentLogo('/assets/images/ihslogo.png');
    setSuperAdminLogoFile(null);
    setSuperAdminLogoPreview('');
    alert('Logo reset to default');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <div className="hidden lg:block">
          <Sidebar 
            isCollapsed={isSidebarCollapsed}
            isSuperAdmin={isSuperAdminUser}
            userRole="admin"
          />
        </div>
        <main 
          className={`flex-1 transition-layout ${
            isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
          }`}
        >
          <div className="p-6 lg:p-8 max-w-7xl mx-auto">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground mb-2">Settings</h1>
              <p className="text-muted-foreground">
                {isSuperAdminUser
                  ? 'Manage user accounts, create new users, and reset passwords'
                  : 'Manage your account settings'}
              </p>
            </div>

            {isSuperAdminUser ? (
              <div className="space-y-6">
                {/* Logo Upload Section */}
                <div className="bg-card rounded-lg border border-border shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Custom Logo</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-6">
                      {/* Current Logo Preview */}
                      <div className="flex-shrink-0">
                        <p className="text-sm font-medium text-foreground mb-2">Current Logo</p>
                        <div className="w-32 h-32 border-2 border-border rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden">
                          <img 
                            src={currentLogo} 
                            alt="Current logo" 
                            className="max-w-full max-h-full object-contain"
                            style={{ mixBlendMode: 'multiply' }}
                          />
                        </div>
                      </div>

                      {/* New Logo Preview */}
                      {superAdminLogoPreview && (
                        <div className="flex-shrink-0">
                          <p className="text-sm font-medium text-foreground mb-2">New Logo Preview</p>
                          <div className="w-32 h-32 border-2 border-primary rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden">
                            <img 
                              src={superAdminLogoPreview} 
                              alt="New logo preview" 
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        </div>
                      )}

                      {/* Upload Controls */}
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground mb-3">
                          Upload a custom logo for your organization. Recommended size: 400x400px. Max file size: 5MB.
                        </p>
                        <div className="space-y-3">
                          <div>
                            <input
                              type="file"
                              id="logo-upload"
                              accept="image/*"
                              onChange={handleSuperAdminLogoChange}
                              className="hidden"
                            />
                            <label
                              htmlFor="logo-upload"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-md cursor-pointer transition-colors border border-border"
                            >
                              <Icon name="Upload" size={18} />
                              Choose Logo File
                            </label>
                            {superAdminLogoFile && (
                              <span className="ml-3 text-sm text-muted-foreground">
                                {superAdminLogoFile.name}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              onClick={handleSuperAdminLogoUpload}
                              disabled={!superAdminLogoFile || superAdminLogoUploading}
                              className="flex items-center gap-2"
                            >
                              <Icon name={superAdminLogoUploading ? "Loader" : "Check"} size={18} />
                              {superAdminLogoUploading ? 'Uploading...' : 'Upload Logo'}
                            </Button>
                            <Button
                              onClick={handleSuperAdminLogoReset}
                              variant="outline"
                              className="flex items-center gap-2"
                            >
                              <Icon name="RotateCcw" size={18} />
                              Reset to Default
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 flex-wrap">
                  <Button
                    onClick={() => setShowCreateAccount(true)}
                    className="flex items-center gap-2"
                  >
                    <Icon name="user-plus" size={18} />
                    Create New Account
                  </Button>
                  <Button
                    onClick={() => setShowChangePassword(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Icon name="lock" size={18} />
                    Change My Password
                  </Button>
                  <Button
                    onClick={() => setShowResetPassword(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Icon name="key" size={18} />
                    Reset Password
                  </Button>
                </div>

                {/* Users List */}
                <div className="bg-card rounded-lg border border-border shadow-sm">
                  <div className="p-6 border-b border-border">
                    <div className="flex items-center justify-between gap-4">
                      <h2 className="text-xl font-semibold text-foreground">All Users</h2>
                      <div className="w-64">
                        <Input
                          type="text"
                          placeholder="Search users..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {usersLoading ? (
                    <div className="p-8 text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <p className="mt-2 text-muted-foreground">Loading users...</p>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No users found
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              National ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Group Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Phone
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Created
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {filteredUsers.map((user) => (
                            <tr key={user.id} className="hover:bg-muted/50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                {user.full_name || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground font-mono">
                                {user.national_id || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {user.group_roles && user.group_roles.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {user.group_roles.map((r) => (
                                      <span
                                        key={r}
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                          r === 'admin'
                                            ? 'bg-blue-100 text-blue-800'
                                            : r === 'clerk'
                                            ? 'bg-amber-100 text-amber-800'
                                            : 'bg-gray-100 text-gray-800'
                                        }`}
                                      >
                                        {r === 'admin' ? 'Admin' : r === 'clerk' ? 'Clerk' : 'Member'}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-sm text-muted-foreground">N/A</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                {user.email || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                {user.phone || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                                    user.role
                                  )}`}
                                >
                                  {getRoleDisplayName(user.role)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                {new Date(user.created_at).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEditUserModal(user)}
                                    className="inline-flex items-center gap-2"
                                  >
                                    <Icon name="Edit" size={16} />
                                    Edit
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openAdminSetPasswordModal(user)}
                                    className="inline-flex items-center gap-2"
                                  >
                                    <Icon name="key" size={16} />
                                    Set Password
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : userProfile?.role === 'group_admin' ? (
              <div className="space-y-6">
                {/* Personal Information Section */}
                <div className="bg-card rounded-lg border border-border shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center">
                        <Icon name="User" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-foreground">Personal Information</h2>
                        <p className="text-sm text-muted-foreground">
                          Update your personal details
                        </p>
                      </div>
                    </div>
                    {!editingPersonalInfo && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Edit button clicked, setting editingPersonalInfo to true');
                          setEditingPersonalInfo(true);
                        }}
                        className="flex items-center gap-2"
                      >
                        <Icon name="Pencil" size={16} />
                        Edit
                      </Button>
                    )}
                  </div>

                  {editingPersonalInfo ? (
                    <div className="space-y-4">
                      {personalInfoError && (
                        <div className="p-3 bg-destructive/10 text-destructive text-sm rounded">
                          {personalInfoError}
                        </div>
                      )}

                      {/* Avatar Upload Section */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Profile Photo
                        </label>
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center">
                              {avatarPreview ? (
                                <img
                                  src={avatarPreview}
                                  alt="Avatar preview"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-primary/10">
                                  <Icon name="User" size={32} color="var(--color-primary)" />
                                </div>
                              )}
                            </div>
                            {avatarPreview && (
                              <button
                                type="button"
                                onClick={handleRemoveAvatar}
                                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                                title="Remove photo"
                              >
                                <Icon name="X" size={14} />
                              </button>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <input
                              ref={avatarInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarChange}
                              className="hidden"
                              id="avatar-upload-admin"
                            />
                            <label
                              htmlFor="avatar-upload-admin"
                              className="cursor-pointer"
                            >
                              <Button
                                type="button"
                                variant="outline"
                                iconName="Upload"
                                iconPosition="left"
                                asChild
                              >
                                <span>{avatarPreview ? 'Change Photo' : 'Upload Photo'}</span>
                              </Button>
                            </label>
                            <p className="text-xs text-muted-foreground">
                              Recommended: Square image, max 5MB (JPG, PNG, GIF, or WebP)
                            </p>
                          </div>
                        </div>
                      </div>

                      <Input
                        label="Full Name"
                        type="text"
                        value={personalInfo.fullName}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, fullName: e.target.value })}
                        placeholder="Enter your full name"
                      />

                      <Input
                        label="Phone Number"
                        type="tel"
                        value={personalInfo.phone}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                        placeholder="Enter your phone number"
                      />

                      <Input
                        label="National ID"
                        type="text"
                        value={personalInfo.nationalId}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, nationalId: e.target.value })}
                        placeholder="Enter your national ID"
                      />

                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPersonalInfo(false);
                            setAvatarFile(null);
                            setAvatarPreview(personalInfo.avatarUrl || null);
                            if (avatarInputRef.current) avatarInputRef.current.value = '';
                            loadPersonalInfo(); // Reset to original values
                            setPersonalInfoError('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="default"
                          onClick={() => handleSavePersonalInfo()}
                          disabled={savingPersonalInfo || uploadingAvatar}
                        >
                          {savingPersonalInfo || uploadingAvatar ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Profile Photo</p>
                        <div className="w-20 h-20 rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center">
                          {personalInfo.avatarUrl ? (
                            <img
                              src={personalInfo.avatarUrl}
                              alt="Profile photo"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  const initials = (personalInfo.fullName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase();
                                  parent.innerHTML = `
                                    <div class="w-full h-full flex items-center justify-center bg-primary/10">
                                      <span class="text-2xl font-semibold text-primary">${initials}</span>
                                    </div>
                                  `;
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-primary/10">
                              <span className="text-2xl font-semibold text-primary">
                                {(personalInfo.fullName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Full Name</p>
                        <p className="text-sm text-foreground">{personalInfo.fullName || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Phone Number</p>
                        <p className="text-sm text-foreground">{personalInfo.phone || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">National ID</p>
                        <p className="text-sm text-foreground">{personalInfo.nationalId || 'Not set'}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Company Profile Section */}
                <div className="bg-card rounded-lg border border-border shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center">
                      <Icon name="Building2" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Company Profile</h2>
                      <p className="text-sm text-muted-foreground">
                        Set your company name and logo. This will be used for all new groups you create.
                      </p>
                    </div>
                  </div>

                  {loadingCompanyProfile ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                        <p className="text-muted-foreground">Loading company profile...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Input
                        label="Company/Organization Name"
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Enter company name"
                      />

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Company Logo
                        </label>
                        <div className="space-y-3">
                          {logoPreview && (
                            <div className="relative inline-block">
                              <img
                                src={logoPreview}
                                alt="Logo preview"
                                className="h-24 w-24 object-contain border border-border rounded-md p-2 bg-muted/50"
                              />
                              {logoFile && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setLogoFile(null);
                                    setLogoPreview(companyLogoUrl || null);
                                    if (companyLogoInputRef.current) companyLogoInputRef.current.value = '';
                                  }}
                                  className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                                  title="Remove new logo"
                                >
                                  <Icon name="X" size={14} />
                                </button>
                              )}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              ref={companyLogoInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleCompanyLogoChange}
                              className="hidden"
                              id="company-logo-upload"
                            />
                            <label
                              htmlFor="company-logo-upload"
                              className="cursor-pointer"
                            >
                              <Button
                                type="button"
                                variant="outline"
                                iconName="Upload"
                                iconPosition="left"
                                asChild
                              >
                                <span>{logoPreview ? 'Change Logo' : 'Upload Logo'}</span>
                              </Button>
                            </label>
                            {companyLogoUrl && !logoFile && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleRemoveCompanyLogo}
                                iconName="Trash2"
                                iconPosition="left"
                              >
                                Remove Logo
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Recommended: Square image, max 5MB (JPG, PNG, GIF, or WebP)
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="default"
                          onClick={() => {
                            if (logoFile) {
                              handleUploadCompanyLogo();
                            } else {
                              handleSaveCompanyProfile();
                            }
                          }}
                          disabled={uploadingLogo}
                        >
                          {uploadingLogo ? 'Saving...' : 'Save Company Profile'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Change Password Section */}
                <div className="bg-card rounded-lg border border-border shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Change Password</h2>
                  <Button
                    onClick={() => setShowChangePassword(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Icon name="Lock" size={18} />
                    Change My Password
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Personal Information Section */}
                <div className="bg-card rounded-lg border border-border shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center">
                        <Icon name="User" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-foreground">Personal Information</h2>
                        <p className="text-sm text-muted-foreground">
                          Update your personal details
                        </p>
                      </div>
                    </div>
                    {!editingPersonalInfo && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Edit button clicked, setting editingPersonalInfo to true');
                          setEditingPersonalInfo(true);
                        }}
                        className="flex items-center gap-2"
                      >
                        <Icon name="Pencil" size={16} />
                        Edit
                      </Button>
                    )}
                  </div>

                  {editingPersonalInfo ? (
                    <div className="space-y-4">
                      {personalInfoError && (
                        <div className="p-3 bg-destructive/10 text-destructive text-sm rounded">
                          {personalInfoError}
                        </div>
                      )}

                      {/* Avatar Upload Section */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Profile Photo
                        </label>
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center">
                              {avatarPreview ? (
                                <img
                                  src={avatarPreview}
                                  alt="Avatar preview"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-primary/10">
                                  <Icon name="User" size={32} color="var(--color-primary)" />
                                </div>
                              )}
                            </div>
                            {avatarPreview && (
                              <button
                                type="button"
                                onClick={handleRemoveAvatar}
                                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                                title="Remove photo"
                              >
                                <Icon name="X" size={14} />
                              </button>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <input
                              ref={avatarInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarChange}
                              className="hidden"
                              id="avatar-upload-member"
                            />
                            <label
                              htmlFor="avatar-upload-member"
                              className="cursor-pointer"
                            >
                              <Button
                                type="button"
                                variant="outline"
                                iconName="Upload"
                                iconPosition="left"
                                asChild
                              >
                                <span>{avatarPreview ? 'Change Photo' : 'Upload Photo'}</span>
                              </Button>
                            </label>
                            <p className="text-xs text-muted-foreground">
                              Recommended: Square image, max 5MB (JPG, PNG, GIF, or WebP)
                            </p>
                          </div>
                        </div>
                      </div>

                      <Input
                        label="Full Name"
                        type="text"
                        value={personalInfo.fullName}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, fullName: e.target.value })}
                        placeholder="Enter your full name"
                      />

                      <Input
                        label="Phone Number"
                        type="tel"
                        value={personalInfo.phone}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                        placeholder="Enter your phone number"
                      />

                      <Input
                        label="National ID"
                        type="text"
                        value={personalInfo.nationalId}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, nationalId: e.target.value })}
                        placeholder="Enter your national ID"
                      />

                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPersonalInfo(false);
                            setAvatarFile(null);
                            setAvatarPreview(personalInfo.avatarUrl || null);
                            if (avatarInputRef.current) avatarInputRef.current.value = '';
                            loadPersonalInfo(); // Reset to original values
                            setPersonalInfoError('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="default"
                          onClick={() => handleSavePersonalInfo()}
                          disabled={savingPersonalInfo || uploadingAvatar}
                        >
                          {savingPersonalInfo || uploadingAvatar ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Profile Photo</p>
                        <div className="w-20 h-20 rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center">
                          {personalInfo.avatarUrl ? (
                            <img
                              src={personalInfo.avatarUrl}
                              alt="Profile photo"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  const initials = (personalInfo.fullName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase();
                                  parent.innerHTML = `
                                    <div class="w-full h-full flex items-center justify-center bg-primary/10">
                                      <span class="text-2xl font-semibold text-primary">${initials}</span>
                                    </div>
                                  `;
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-primary/10">
                              <span className="text-2xl font-semibold text-primary">
                                {(personalInfo.fullName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Full Name</p>
                        <p className="text-sm text-foreground">{personalInfo.fullName || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Phone Number</p>
                        <p className="text-sm text-foreground">{personalInfo.phone || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">National ID</p>
                        <p className="text-sm text-foreground">{personalInfo.nationalId || 'Not set'}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Change Password Section */}
                <div className="bg-card rounded-lg border border-border shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Change Password</h2>
                  <Button
                    onClick={() => setShowChangePassword(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Icon name="Lock" size={18} />
                    Change My Password
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Create Account Modal */}
      {showCreateAccount && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-card rounded-lg shadow-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Create New Account</h2>
              <button
                onClick={() => setShowCreateAccount(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateAccount} className="space-y-4">
              {createAccountErrors.general && (
                <div className="p-3 bg-destructive/10 text-destructive text-sm rounded">
                  {createAccountErrors.general}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Full Name *
                </label>
                <Input
                  type="text"
                  value={createAccountData.fullName}
                  onChange={(e) =>
                    setCreateAccountData({ ...createAccountData, fullName: e.target.value })
                  }
                  required
                />
                {createAccountErrors.fullName && (
                  <p className="text-sm text-destructive mt-1">{createAccountErrors.fullName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Email (or Phone)
                </label>
                <Input
                  type="text"
                  value={createAccountData.email}
                  onChange={(e) =>
                    setCreateAccountData({ ...createAccountData, email: e.target.value })
                  }
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Phone (if no email)
                </label>
                <Input
                  type="tel"
                  value={createAccountData.phone}
                  onChange={(e) =>
                    setCreateAccountData({ ...createAccountData, phone: e.target.value })
                  }
                  placeholder="+6781234567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Role *</label>
                <select
                  value={createAccountData.role}
                  onChange={(e) =>
                    setCreateAccountData({
                      ...createAccountData,
                      role: e.target.value as 'group_admin' | 'member',
                    })
                  }
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="member">Member</option>
                  <option value="group_admin">Group Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Password *
                </label>
                <Input
                  type="password"
                  value={createAccountData.password}
                  onChange={(e) =>
                    setCreateAccountData({ ...createAccountData, password: e.target.value })
                  }
                  required
                  minLength={6}
                />
                {createAccountErrors.password && (
                  <p className="text-sm text-destructive mt-1">{createAccountErrors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Confirm Password *
                </label>
                <Input
                  type="password"
                  value={createAccountData.confirmPassword}
                  onChange={(e) =>
                    setCreateAccountData({
                      ...createAccountData,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                />
                {createAccountErrors.confirmPassword && (
                  <p className="text-sm text-destructive mt-1">
                    {createAccountErrors.confirmPassword}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateAccount(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createAccountLoading} className="flex-1">
                  {createAccountLoading ? 'Creating...' : 'Create Account'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-card rounded-lg shadow-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Reset Password</h2>
              <button
                onClick={() => setShowResetPassword(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              {resetPasswordError && (
                <div className="p-3 bg-destructive/10 text-destructive text-sm rounded">
                  {resetPasswordError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Email or Phone Number *
                </label>
                <Input
                  type="text"
                  value={resetPasswordData.emailOrPhone}
                  onChange={(e) =>
                    setResetPasswordData({ ...resetPasswordData, emailOrPhone: e.target.value })
                  }
                  placeholder="email@example.com or +6781234567"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  A password reset link will be sent to the user's email or phone.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowResetPassword(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={resetPasswordLoading} className="flex-1">
                  {resetPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Super Admin: Set User Password Modal */}
      {showAdminSetUserPassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-card rounded-lg shadow-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Set User Password</h2>
              <button
                onClick={() => setShowAdminSetUserPassword(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="mb-4 text-sm text-muted-foreground">
              Setting a password here will immediately change the user’s login password.
              <div className="mt-2">
                <span className="font-medium text-foreground">User ID:</span>{' '}
                <span className="font-mono">{adminPasswordTargetUser?.id}</span>
              </div>
              {adminPasswordTargetUser?.full_name && (
                <div className="mt-1">
                  <span className="font-medium text-foreground">Name:</span>{' '}
                  {adminPasswordTargetUser.full_name}
                </div>
              )}
              {adminPasswordTargetUser?.national_id && (
                <div className="mt-1">
                  <span className="font-medium text-foreground">National ID:</span>{' '}
                  <span className="font-mono">{adminPasswordTargetUser.national_id}</span>
                </div>
              )}
              {adminPasswordTargetUser?.group_roles && adminPasswordTargetUser.group_roles.length > 0 && (
                <div className="mt-1">
                  <span className="font-medium text-foreground">Group Role:</span>{' '}
                  <span className="capitalize">{adminPasswordTargetUser.group_roles.join(', ')}</span>
                </div>
              )}
              {(adminPasswordTargetUser?.email || adminPasswordTargetUser?.phone) && (
                <div className="mt-1">
                  <span className="font-medium text-foreground">User:</span>{' '}
                  {adminPasswordTargetUser?.email || adminPasswordTargetUser?.phone}
                </div>
              )}
            </div>

            <form onSubmit={handleAdminSetUserPassword} className="space-y-4">
              {adminSetPasswordError && (
                <div className="p-3 bg-destructive/10 text-destructive text-sm rounded">
                  {adminSetPasswordError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  New Password *
                </label>
                <Input
                  type="password"
                  value={adminSetPasswordData.newPassword}
                  onChange={(e) =>
                    setAdminSetPasswordData({ ...adminSetPasswordData, newPassword: e.target.value })
                  }
                  required
                  minLength={8}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum 8 characters. Share it securely and ask the user to change it after login.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Confirm New Password *
                </label>
                <Input
                  type="password"
                  value={adminSetPasswordData.confirmPassword}
                  onChange={(e) =>
                    setAdminSetPasswordData({
                      ...adminSetPasswordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                  minLength={8}
                />
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const temp = generateTempPassword();
                    setAdminSetPasswordData({ newPassword: temp, confirmPassword: temp });
                  }}
                  className="flex items-center gap-2"
                >
                  <Icon name="Shuffle" size={18} />
                  Generate
                </Button>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAdminSetUserPassword(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={adminSetPasswordLoading}>
                    {adminSetPasswordLoading ? 'Saving...' : 'Set Password'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Super Admin: Edit User Modal */}
      {showEditUser && editingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-card rounded-lg shadow-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Edit User Information</h2>
              <button
                onClick={closeEditUserModal}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="mb-4 text-sm text-muted-foreground">
              <div>
                <span className="font-medium text-foreground">User ID:</span>{' '}
                <span className="font-mono">{editingUser.id}</span>
              </div>
              {editingUser.email && (
                <div className="mt-1">
                  <span className="font-medium text-foreground">Email:</span>{' '}
                  {editingUser.email}
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveUserEdit(); }} className="space-y-4">
              {editUserError && (
                <div className="p-3 bg-destructive/10 text-destructive text-sm rounded">
                  {editUserError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Full Name *
                </label>
                <Input
                  type="text"
                  value={editUserData.fullName}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, fullName: e.target.value })
                  }
                  required
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Phone Number
                </label>
                <Input
                  type="text"
                  value={editUserData.phone}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, phone: e.target.value })
                  }
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  National ID
                </label>
                <Input
                  type="text"
                  value={editUserData.nationalId}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, nationalId: e.target.value })
                  }
                  placeholder="Enter national ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Role *
                </label>
                <select
                  value={editUserData.role}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, role: e.target.value as any })
                  }
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                >
                  <option value="group_member">Group Member</option>
                  <option value="group_creator">Group Creator</option>
                  <option value="group_admin">Group Admin</option>
                  <option value="group_clerk">Group Clerk</option>
                  <option value="super_admin">Super Admin</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Changing a user's role will affect their access permissions.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeEditUserModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={editUserLoading} className="flex-1">
                  {editUserLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-card rounded-lg shadow-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Change My Password</h2>
              <button
                onClick={() => setShowChangePassword(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              {changePasswordErrors.general && (
                <div className="p-3 bg-destructive/10 text-destructive text-sm rounded">
                  {changePasswordErrors.general}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Current Password *
                </label>
                <Input
                  type="password"
                  value={changePasswordData.currentPassword}
                  onChange={(e) =>
                    setChangePasswordData({
                      ...changePasswordData,
                      currentPassword: e.target.value,
                    })
                  }
                  required
                />
                {changePasswordErrors.currentPassword && (
                  <p className="text-sm text-destructive mt-1">
                    {changePasswordErrors.currentPassword}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  New Password *
                </label>
                <Input
                  type="password"
                  value={changePasswordData.newPassword}
                  onChange={(e) =>
                    setChangePasswordData({
                      ...changePasswordData,
                      newPassword: e.target.value,
                    })
                  }
                  required
                  minLength={6}
                />
                {changePasswordErrors.newPassword && (
                  <p className="text-sm text-destructive mt-1">
                    {changePasswordErrors.newPassword}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Confirm New Password *
                </label>
                <Input
                  type="password"
                  value={changePasswordData.confirmPassword}
                  onChange={(e) =>
                    setChangePasswordData({
                      ...changePasswordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                />
                {changePasswordErrors.confirmPassword && (
                  <p className="text-sm text-destructive mt-1">
                    {changePasswordErrors.confirmPassword}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowChangePassword(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={changePasswordLoading} className="flex-1">
                  {changePasswordLoading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;

